/**
 * ConfigurationManager - Level 1 Component
 *
 * RESPONSIBILITY:
 * Determines when new configurations are needed and manages configuration lifecycle.
 *
 * HIERARCHY:
 * - Reports to: ProjectOrchestrator
 * - Manages: Configuration creation and cleanup logic
 * - Peers: DisplayCoordinator
 *
 * KEY OPERATIONS:
 * - shouldCreateConfiguration: Determines if configuration changes warrant new configuration
 * - createConfiguration: Builds new configuration with proper setup
 * - cleanupOldConfigurations: Manages configuration retention and cleanup
 *
 * NAVIGATION:
 * - For orchestration context, see: ./project-orchestrator.ts
 * - For configuration processing, see: ~/candidate-processing/configuration-processor.ts
 */

import { logger } from '~/services/logging';
import type {
  BondSeries,
  Candidate,
  Configuration,
  DRFInputsForm,
  MillageInputsForm,
  Project,
} from '~/types';
import { generateConfigurationName } from '~/utils/date-utils';

export interface ConfigurationContext {
  scenarioId?: string;
  actionId?: string;
}

export class ConfigurationManager {
  /**
   * Determines if a new configuration should be created based on configuration changes
   */
  shouldCreateConfiguration(
    project: Project,
    allBondSeries: BondSeries[],
    isFirstConfiguration: boolean
  ): boolean {
    // Always create for first configuration
    if (isFirstConfiguration) {
      return true;
    }

    const currentConfigurations = project.configurations || [];
    const lastConfiguration = currentConfigurations[currentConfigurations.length - 1];

    if (!lastConfiguration) {
      return true;
    }

    // Check for configuration changes that warrant new configuration
    const configChanged = this.hasConfigurationChanged(project, lastConfiguration, allBondSeries);

    return configChanged;
  }

  /**
   * Checks if configuration has changed between project and last configuration
   */
  private hasConfigurationChanged(
    project: Project,
    lastConfiguration: Configuration,
    newBondSeries: BondSeries[]
  ): boolean {
    // Check bond series changes
    if (this.haveBondSeriesChanged(lastConfiguration.bondSeries || [], newBondSeries)) {
      logger.L1.debug('Bond series configuration changed');
      return true;
    }

    // Check millage changes
    if (
      project.millageInputs &&
      this.haveMillageInputsChanged(lastConfiguration.millageInputs, project.millageInputs)
    ) {
      logger.L1.debug('Millage configuration changed');
      return true;
    }

    // Check DRF changes
    if (
      project.drfInputs &&
      this.haveDRFInputsChanged(lastConfiguration.drfInputs, project.drfInputs)
    ) {
      logger.L1.debug('DRF configuration changed');
      return true;
    }

    return false;
  }

  /**
   * Compares bond series arrays for changes
   */
  private haveBondSeriesChanged(oldBonds: BondSeries[], newBonds: BondSeries[]): boolean {
    // Different count means changed
    if (oldBonds.length !== newBonds.length) {
      return true;
    }

    // Check each bond for changes
    for (let i = 0; i < oldBonds.length; i++) {
      const oldBond = oldBonds[i];
      const newBond = newBonds.find((b) => b.id === oldBond.id);

      if (!newBond) {
        return true; // Bond removed
      }

      // Check key properties for changes
      interface InterestRateEscalation {
        startRate: number;
        incrementRate: number;
        maxRate: number;
      }

      const oldBondWithEscalation = oldBond as BondSeries & {
        __interestRateEscalation?: InterestRateEscalation;
      };
      const newBondWithEscalation = newBond as BondSeries & {
        __interestRateEscalation?: InterestRateEscalation;
      };

      const oldEscalation = oldBondWithEscalation.__interestRateEscalation;
      const newEscalation = newBondWithEscalation.__interestRateEscalation;

      // Compare target information
      const oldTarget = oldBond.targetInformation?.principalTargetAmount;
      const newTarget = newBond.targetInformation?.principalTargetAmount;
      const oldTargetType = oldBond.targetInformation?.targetType;
      const newTargetType = newBond.targetInformation?.targetType;

      if (
        oldBond.name !== newBond.name ||
        oldBond.issueDate !== newBond.issueDate ||
        oldBond.firstInterestPaymentDate !== newBond.firstInterestPaymentDate ||
        oldBond.firstPrincipalPaymentDate !== newBond.firstPrincipalPaymentDate ||
        oldBond.finalPrincipalPaymentDate !== newBond.finalPrincipalPaymentDate ||
        oldEscalation?.startRate !== newEscalation?.startRate ||
        oldEscalation?.maxRate !== newEscalation?.maxRate ||
        oldEscalation?.incrementRate !== newEscalation?.incrementRate ||
        oldTarget !== newTarget ||
        oldTargetType !== newTargetType
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Compares millage inputs for changes
   */
  private haveMillageInputsChanged(
    oldInputs: MillageInputsForm,
    newInputs: MillageInputsForm
  ): boolean {
    return (
      oldInputs.peakMillage !== newInputs.peakMillage ||
      oldInputs.peakMillageDuration !== newInputs.peakMillageDuration ||
      oldInputs.minimumMillage !== newInputs.minimumMillage ||
      oldInputs.minimumMillageDuration !== newInputs.minimumMillageDuration
    );
  }

  /**
   * Compares DRF inputs for changes
   */
  private haveDRFInputsChanged(oldInputs: DRFInputsForm, newInputs: DRFInputsForm): boolean {
    return (
      oldInputs.startingBalance !== newInputs.startingBalance ||
      oldInputs.minimumBalance !== newInputs.minimumBalance ||
      oldInputs.maximumBalance !== newInputs.maximumBalance
    );
  }

  /**
   * Finds an existing configuration that matches the proposed configuration
   * Compares bond series, millage inputs, and DRF inputs
   * Returns the matching configuration or null if no match found
   */
  findMatchingConfiguration(
    proposedBondSeries: BondSeries[],
    proposedMillageInputs: MillageInputsForm,
    proposedDRFInputs: DRFInputsForm,
    existingConfigurations: Configuration[]
  ): Configuration | null {
    for (const config of existingConfigurations) {
      const bondsMatch = !this.haveBondSeriesChanged(config.bondSeries || [], proposedBondSeries);
      const millageMatch = !this.haveMillageInputsChanged(
        config.millageInputs,
        proposedMillageInputs
      );
      const drfMatch = !this.haveDRFInputsChanged(config.drfInputs, proposedDRFInputs);

      if (bondsMatch && millageMatch && drfMatch) {
        logger.L1.debug(`Found matching configuration: ${config.id}`, {
          configId: config.id,
          orderId: config.orderId,
        });
        return config;
      }
    }

    return null;
  }

  /**
   * Creates a new configuration with the provided setup
   */
  createConfiguration(
    project: Project,
    allBondSeries: BondSeries[],
    currentConfigurationCount: number,
    context?: ConfigurationContext
  ): Configuration {
    // Generate deterministic ID for scenario actions, random for others
    const configurationId =
      context?.scenarioId && context?.actionId
        ? `${context.scenarioId}-${context.actionId}`
        : this.generateConfigurationId();

    const sourceInputs = {
      millageInputs: project.millageInputs || {
        peakMillage: '',
        peakMillageDuration: '',
        minimumMillage: '',
        minimumMillageDuration: '',
      },
      drfInputs: project.drfInputs || {
        startingBalance: '',
        maximumBalance: '',
        minimumBalance: '',
      },
    };

    const orderId = currentConfigurationCount + 1;
    const existingConfigurations = project.configurations || [];
    const newConfiguration: Configuration = {
      id: configurationId,
      orderId,
      createdAt: new Date().toISOString(), // Set creation date for sorting
      name: generateConfigurationName(orderId, undefined, existingConfigurations), // Set default name in YYMMDD.HHMM or YYMMDD.HHMM.[order] format
      totalCandidates: 0,
      bondSeries: [...allBondSeries], // Copy all bonds
      candidates: [],
      millageInputs: sourceInputs.millageInputs,
      drfInputs: sourceInputs.drfInputs,
      scenarioId: context?.scenarioId,
      actionId: context?.actionId,
    };

    logger.L1.info(`Created configuration ${configurationId}`, {
      configurationId,
      orderId: newConfiguration.orderId,
      scenarioId: context?.scenarioId,
      actionId: context?.actionId,
    });

    return newConfiguration;
  }

  /**
   * Generates a unique configuration ID (UUID format for database compatibility)
   */
  private generateConfigurationId(): string {
    return crypto.randomUUID();
  }

  /**
   * Cleans up old configurations to prevent storage issues
   */
  cleanupOldConfigurations(
    configurations: Configuration[],
    maxConfigurations: number
  ): Configuration[] {
    if (configurations.length <= maxConfigurations) {
      return configurations;
    }

    const cleanedConfigurations = configurations.slice(-maxConfigurations);

    logger.L1.info('Cleaned up old configurations', {
      kept: cleanedConfigurations.length,
      original: configurations.length,
      removed: configurations.length - cleanedConfigurations.length,
    });

    return cleanedConfigurations;
  }

  /**
   * Strips detailed processing data from configurations to reduce storage
   */
  stripProcessingData(configuration: Configuration): Configuration {
    const stripped = { ...configuration };

    // Keep essential data but remove detailed processing results
    if (stripped.candidates) {
      stripped.candidates = stripped.candidates.map(
        (candidate) =>
          ({
            ...candidate,
            // Remove heavy data like cash flows and detailed calculations
            cashFlows: undefined,
            taxYearBasedProjectPlan: undefined,
            revenueMap: undefined,
            optimizationHistory: undefined,
          }) as unknown as Candidate
      );
    }

    return stripped;
  }

  /**
   * Validates a configuration has required data for processing
   */
  validateConfigurationForProcessing(configuration: Configuration): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
      errors.push('Configuration must have at least one bond series');
    }

    if (!configuration.millageInputs.peakMillage) {
      errors.push('Peak millage is required');
    }

    if (!configuration.drfInputs.startingBalance) {
      errors.push('DRF starting balance is required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
